import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { Sensor, SensorInfo, SensorReadingCriteria, SensorReadingType, SensorSearchCriteria, TimeZoneDisplay, UserGroup, VirtualParameterValueCriteria, VirtualReadingRow, VirtualReadingsResult, VirtualSensor, VirtualSensorFunction, VirtualSensorParameter, VirtualSourceReading } from '@models';
import { GpsInfo } from '@models/Map/gpsInfo.model';
import { DateRanges } from '@models/ranges';
import { SensorService } from '@services/sensor.service';
import { CommonShared } from 'app/shared/classes/common';
import { SensorMapping } from 'app/shared/classes/sensorMapping';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
};

export interface VirtualSensorModalParams {
  sensor: Sensor,
  allSensors: Sensor[],
  allGpsInfos: GpsInfo[],
  sensorReadingTypes: SensorReadingType[]
 }

@Component({
  standalone: false,
  selector: 'virtual-sensor-modal',
  templateUrl: './virtual-sensor-modal.component.html',
  styleUrls: ['./virtual-sensor-modal.component.scss']
})

export class VirtualSensorModalComponent implements OnInit, VirtualSensorModalParams {

  @Input() sensor: Sensor;
  @Input() allGpsInfos: GpsInfo[] = [];
  @Input() sensorReadingTypes: SensorReadingType[];
  @Input() allSensors: Sensor[];

  common = new CommonShared();
  mapping = new SensorMapping();

  modalDragOn: boolean = false;
  virtualSensor: VirtualSensor;

  sensorsForVirtualCols = [
    { field: 'name', header: 'Variable Name', width: '14%' },
    { field: 'sensorName', header: 'Sensor Name/UUID', width: '25%' },
    { field: 'useLastXReadings', header: 'Use Last X & Aggr', width: '28%' },
    { field: 'readingType', header: 'Reading Type', width: '20%' },
    { field: 'testValue', header: 'Test Value', width: '13%' },
  ];
  
  useLastXMathOptions:string[] = [ "cnt", "sum", "avg", "med", "min", "max" ];

  processingVirtualSensorReadings: boolean = false;
  searchSensorForVirtualText: string = '';
  waitForSensorSearch: boolean = false;
  waitForFunctionTest: boolean = false;
  selectedUserGroup: UserGroup;
  processingVirtualSensorReadingsResults: string = '';
  collectingDateRangeFor: string = '';
  dataRangeSelectorTitle: string = '';
  dateRangeSelectorRef: BsModalRef;

  // What the last "Generate Readings" run created, for the table in the popup. The source readings
  // under each value are shown only while the checkbox is ticked (a display toggle: no re-run needed).
  generateResult: VirtualReadingsResult | null = null;
  showSourceReadings: boolean = false;

  public invalidDates: moment.Moment[] = [];
  public selectedDateRange: DateRange = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };

  isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));

  ranges = DateRanges;

  // "Delay Calculating For" options. Only delays that are exact multiples of the selected
  // frequency are offered (see computeValidDelayOptions) so the delayed target always lands
  // on the interval grid; stored value is in minutes.
  delayOptions: { name: string, value: number }[] = [
    { name: 'No delay', value: 0 },
    { name: '30 min', value: 30 },
    { name: '1 hr', value: 60 },
    { name: '2 hr', value: 120 },
    { name: '4 hr', value: 240 },
    { name: '8 hr', value: 480 },
    { name: '12 hr', value: 720 },
    { name: '16 hr', value: 960 },
    { name: '1 day', value: 1440 },
  ];
  validDelayOptions: { name: string, value: number }[] = [];

  // Daily frequency target (UTC). Minute 59 lets a daily value land just before midnight in the sensor's time zone,
  // e.g. US Central => hour 4, min 59.
  hourOfDayOptions: number[] = Array.from({ length: 24 }, (_, i) => i);
  minuteOfHourOptions: number[] = [59, 0, 1];


  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService) { }
  

  ngOnInit(): void {
    this.virtualSensor = this.sensor.virtualSensor;
    if (this.virtualSensor != null) {
      this.virtualSensor.calculateMinuteOfHour ??= 0;
      this.buildOutVirtualSensorsParameters(this.virtualSensor);
      this.computeValidDelayOptions();
    }
  }


  computeValidDelayOptions() {
    var freq = this.virtualSensor.calculateFreq;
    this.validDelayOptions = this.delayOptions.filter(o => o.value === 0 || (freq > 0 && o.value % freq === 0));
  }


  // When the frequency changes, prune the delay list to multiples of the new frequency and
  // reset the selection to "No delay" if the current delay is no longer valid.
  onCalculateFreqChange() {
    this.computeValidDelayOptions();
    if (this.virtualSensor.calculateDelay !== 0 && !this.validDelayOptions.some(o => o.value === this.virtualSensor.calculateDelay)) {
      this.virtualSensor.calculateDelay = 0;
    }
  }

  
  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnDestroy(): void {   
    this.virtualSensor.parameters = [...this.virtualSensor.parameters.filter(x => x.name != undefined && x.name != '')];
    this.virtualSensor.parameters.forEach(x => x.id = 0 );
    this.virtualSensor.functions.forEach(x => x.id = 0 );
    this.sensor.virtualSensor = this.virtualSensor;
  }


  searchSensorsForVirtual() {
    if (this.searchSensorForVirtualText == undefined || this.searchSensorForVirtualText == '') {
      return;
    }

    this.waitForSensorSearch = true;
    const searchLimit = 100;

    var searchCriteria: SensorSearchCriteria = {
      searchText: this.searchSensorForVirtualText.toLowerCase(),
      maxResultsCount: searchLimit
    }

    this._sensorService.getSensorsWithReadingTypesUsingSearch(searchCriteria).subscribe(
      results => {
        if (results != null) {
          var temp = this.selectedUserGroup;
          this.buildSensorList4VirtualParameters(this.mapping.mapSensorInfos2Sensors(results, this.allGpsInfos));
          this.selectedUserGroup = temp;
        }
        else {
          this.toastr.warning('No search results returned.');
        }
        this.waitForSensorSearch = false;
        return;
      },
      err => {
        this.toastr.error('Error occurred while searching for sensors.');
        this.waitForSensorSearch = false;
      });
  }


  getSensorWithSensorId(sensorId: number): Sensor {
    var sensor = this.allSensors.find(x => x.id == sensorId);
    return sensor
  }


  buildOutVirtualSensorsParameters(virtualSensor: VirtualSensor) {
    virtualSensor.parameters.forEach(x => {
        x.sensorName = this.common.getSensorDisplayName(this.getSensorWithSensorId(x.sensorId));
        var readingType = this.sensorReadingTypes.find(y => y.id == x.readingTypeId);
        if (readingType != undefined) {
          x.readingType = readingType.type;
        }
        else {
          x.readingType = 'unknown';
        }
    });

    this.sortVirtualSensorParameters(virtualSensor.parameters);
  }


  buildSensorList4VirtualParameters(sensors: Sensor[]) {
    this.virtualSensor.parameters = [...this.virtualSensor.parameters.filter(x => x.name != undefined && x.name != '')];
    var existingSensorIds = this.virtualSensor.parameters.map(x => x.sensorId);
    var existingReadingTypeIds = this.virtualSensor.parameters.map(x => x.readingTypeId);
    var count = 0;

    sensors.forEach(sensor => {
      sensor.sensorReadingTypes.forEach(readingType => {

        if (!existingSensorIds.includes(sensor.id) || !existingReadingTypeIds.includes(readingType.id)) {
          count--;
          var newVirtualSensorParameter: VirtualSensorParameter = {
            id: count,
            name: '',
            sensorId: sensor.id,
            readingTypeId: readingType.id,
            sensorName: this.common.getSensorDisplayName(sensor),
            readingType: readingType.type,
            testValue: 0,
            useLastXReadings: 0,
            useLastXAggr: '',
            virtualSensorId: 0
          }
          this.virtualSensor.parameters.push(newVirtualSensorParameter);
        }
      })
    });

    this.sortVirtualSensorParameters(this.virtualSensor.parameters);
  }


  sortVirtualSensorParameters(virtualSensorParameters: VirtualSensorParameter[]) {
    virtualSensorParameters.sort((a, b) => {
      if (b.name == undefined || b.name == '') {
        return -1;
      }
      if (b.name < a.name) {
        return 1;
      }
    });
  }


  virtualSensorParametersChanged(parameter: VirtualSensorParameter) {
    if (parameter.name == undefined || parameter.name == '') {
      return;
    }

    var errFlag = false;

    while (parameter.name.length > 0 && Number.isInteger(parseInt(parameter.name.charAt(0)))) {
      parameter.name = parameter.name.substring(1, parameter.name.length - 1);
      if (parameter.name == '') return;
      errFlag = true;
    }

    if (errFlag) {
      this.toastr.warning('Varable names cannot start with a number. Removing leading number(s).');
    }

    var errFlag = false;

    while (this.isDuplicateVirtualSensorParameterName(parameter)) {
      parameter.name = parameter.name.substring(0, parameter.name.length - 1);
      if (parameter.name == '') return;
      errFlag = true;
    }

    if (errFlag) {
      this.toastr.warning('Duplicate variable names not allowed. Shortened current variable.');
    }

    this.virtualSensor.parameters = [...this.virtualSensor.parameters];
  }


  isDuplicateVirtualSensorParameterName(parameter: VirtualSensorParameter): boolean {
    var errFlag = false;
    this.virtualSensor.parameters.forEach(x => {
      if (x.name.toLowerCase() == parameter.name.toLowerCase() && x.id != parameter.id) {
        errFlag = true;
      }
    });
    return errFlag;
  }


  testVirtualSensorFunctionWithValues(index: number) {
    if (this.virtualSensor.functions[index].equation == "") {
      this.toastr.warning('No equation given. Nothing to test');
      return;
    }

    this.waitForFunctionTest = true;

    this.virtualSensor.functions.forEach(x => { if (x.testResults != undefined && x.testResults.toLowerCase() == "testing") x.testResults = ""; });
    this.virtualSensor.functions[index].testResults = "testing";

    this.sensor.virtualSensor.parameters = this.sensor.virtualSensor.parameters.filter(x => x.name != undefined && x.name != '');
    var sensorInfo: SensorInfo = this.mapping.mapSensor2SensorInfo(this.sensor);
    //sensorInfo.virtualSensorInfo.parameters = sensorInfo.virtualSensorInfo.parameters.filter(x => x.name != undefined && x.name != '');

    this._sensorService.testVirtualSensorFunction(sensorInfo).subscribe(
      results => {
        if (results != null) {
          this.virtualSensor.functions[index].testResults = results.toString();
        }
        else {
          this.toastr.warning('No test results returned.');
        }
        this.waitForFunctionTest = false;
        return;
      },
      err => {
        this.toastr.error('Error occurred while testing equation.');
        this.waitForFunctionTest = false;
      });
  }


  getDateRangesForNewOrDelete(template: TemplateRef<any>, mode: string) {
    this.processingVirtualSensorReadingsResults = '';
    this.generateResult = null;
    this.collectingDateRangeFor = mode;

    if (mode == 'new') {
      this.dataRangeSelectorTitle = 'Generate New Virtual Sensor Readings';
    }
    else {
      this.dataRangeSelectorTitle = 'Delete Existing Virtual Sensor Readings';
    }

    this.dateRangeSelectorRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  // A new date range makes the table of the previous run out of date.
  datesUpdated() {
    this.generateResult = null;
    this.processingVirtualSensorReadingsResults = '';
  }


  ProcessNewOrDelete() {
    var readingCriteria: SensorReadingCriteria = {
      sensorId: this.sensor.id,
      startDate: this.selectedDateRange.startDate.startOf('day').format('YYYY-MM-DD HH:mm'), 
      endDate: this.selectedDateRange.endDate.endOf('day').format('YYYY-MM-DD HH:mm'),
      timeZoneDisplay: TimeZoneDisplay.UTC,
      timeZoneOffset: 0
    }

    if (this.collectingDateRangeFor == "new") {
      this.generateNewVirtualSensorReadings(readingCriteria);
    }
    else {
      this.deleteExistingVirtualSensorReadings(readingCriteria);
    }
  }


  generateNewVirtualSensorReadings(readingCriteria: SensorReadingCriteria) {
    this.processingVirtualSensorReadings = true;
    this.generateResult = null;

    this._sensorService.generateNewVirtualSensorReadingsWithDetails(readingCriteria).subscribe(
      results => {
        if (results != null) {
          if (results.message != undefined && results.message !== '') {
            // Nothing could be generated (bad sensor, an error on the server): show why, as before.
            this.processingVirtualSensorReadingsResults = results.message;
            this.toastr.warning(results.message);
          } else {
            this.generateResult = results;

            // Equations whose inputs had no readings are not created; say so (the table names what each one is missing).
            const missing = results.missing ?? 0;
            if (missing > 0) {
              this.processingVirtualSensorReadingsResults = 'Generated: ' + results.created + '. Not generated: ' + missing + ' (missing data, see the greyed rows).';
              this.toastr.warning('Readings created: ' + results.created + '. ' + missing + ' could not be calculated because input data is missing.');
            } else {
              this.processingVirtualSensorReadingsResults = 'Generated: ' + results.created;
              this.toastr.success('Readings created: ' + results.created);
            }
          }
        }
        this.processingVirtualSensorReadings = false;
        return;
      },
      err => {
        this.processingVirtualSensorReadings = false;
        this.toastr.error('Error generating virtual sensor data.');
      });
  }


  // Which variable rows are waiting for the "calculate current value" icon's request, so each icon spins on its own.
  calculatingTestValueFor = new Set<VirtualSensorParameter>();

  // The calculator icon beside a variable's Test Value: fill the box with the value the variable would take right
  // now, using this row's own "Use last X" and "Aggr" (as currently shown, saved or not).
  calculateTestValue(event: Event, rowData: VirtualSensorParameter) {
    event.stopPropagation();      // the cell is click-to-edit; the icon must not open the box

    if (this.calculatingTestValueFor.has(rowData)) {
      return;
    }

    const useLastX = Number(rowData.useLastXReadings);
    const criteria: VirtualParameterValueCriteria = {
      sensorId: rowData.sensorId,
      readingTypeId: rowData.readingTypeId,
      useLastXReadings: (isNaN(useLastX) || useLastX < 0) ? 0 : Math.floor(useLastX),
      useLastXAggr: (rowData.useLastXAggr ?? '').trim().toLowerCase()
    };

    this.calculatingTestValueFor.add(rowData);

    this._sensorService.calculateVirtualParameterValue(criteria).subscribe(
      result => {
        this.calculatingTestValueFor.delete(rowData);

        if (result == null) {
          this.toastr.warning('The current value could not be calculated.');
          return;
        }

        if (result.value == null) {
          // Leave the number that is there: it may be one the user typed on purpose.
          this.toastr.warning(result.message ?? 'The current value could not be calculated.');
          return;
        }

        // The Test Value box holds 10 characters.
        rowData.testValue = Number(parseFloat(result.value.toPrecision(7)).toString().slice(0, 10));

        const what = (result.aggregation === 'latest')
          ? 'Newest reading'
          : `${result.aggregation} of ${result.readingCount} reading${result.readingCount === 1 ? '' : 's'} in the last ${criteria.useLastXReadings} minutes`;
        const when = (result.latestReadingDate != null) ? ` (latest ${moment.utc(result.latestReadingDate).format('MM/DD/YYYY HH:mm')} UTC)` : '';
        this.toastr.info(`${rowData.name}: ${what}${when}`);
      },
      err => {
        this.calculatingTestValueFor.delete(rowData);
        this.toastr.error('Error calculating the current value.');
      });
  }


  // The icon's hover text, following the row's current settings.
  calculateTestValueTitle(rowData: VirtualSensorParameter): string {
    const useLastX = Number(rowData.useLastXReadings);
    if (isNaN(useLastX) || useLastX <= 0) {
      return 'Calculate the current value: the newest reading';
    }
    const aggr = (rowData.useLastXAggr ?? '').trim() === '' ? 'avg' : rowData.useLastXAggr.trim();
    return `Calculate the current value: ${aggr} of the last ${useLastX} minutes`;
  }


  // The table's rows in display order: each timestamp's row, and under a created one its source readings.
  get generateResultRows(): VirtualReadingRow[] {
    return this.generateResult?.rows ?? [];
  }


  // Source rows are listed oldest first, grouped by the equation variable they fed.
  sourcesOf(row: VirtualReadingRow): VirtualSourceReading[] {
    return row.sources ?? [];
  }


  trackByRow(index: number, row: VirtualReadingRow): string {
    return row.timestamp + '|' + row.readingType;
  }


  deleteExistingVirtualSensorReadings(readingCriteria: SensorReadingCriteria) {
    this.processingVirtualSensorReadings = true;

    this._sensorService.deleteSensorReadingsForReadingTypes(readingCriteria).subscribe(
      results => {
        if (results != null) {
          this.processingVirtualSensorReadingsResults = 'Deleted: ' + results;
          this.toastr.success('Number Readings deleted: ' + results);
        }
        this.processingVirtualSensorReadings = false;
        return;
      },
      err => {
        this.processingVirtualSensorReadings = false;
        this.toastr.error('Error occurred while deleting virtual sensor readings');
      });
  }


  addVirtualFunction() {
    var newVirtulSensorFunction: VirtualSensorFunction = {
      equation: '',
      readingType: '',
      testResults: '',
      id: 0,
      virtualSensorId: 0
    }
    this.virtualSensor.functions.push(newVirtulSensorFunction);
  }


  delVirtualFunction(index: number) {
    this.virtualSensor.functions.splice(index, 1);
    this.virtualSensor.functions = [...this.virtualSensor.functions];
  }


  onVirtulSensorEnable(value: any) {
    this.virtualSensor.isDisabled = !value.checked;
  }


}
